import Head from 'next/head';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { route } from '@services/symfony';
import { gsspGetLocale } from '@utils/gssp';

import { SanitizeHTML } from '@components/common/SanitizeHTML/SanitizeHTML';
import {
  ChatTypingDots, Button, Chip,
} from '@components/common';
import { useChatComposer } from '@hooks/useChatComposer/useChatComposer';
import React, {
  useEffect, useRef, useState, useMemo, useCallback,
} from 'react';

import {
  PaperAirplaneIcon, MicrophoneIcon, ArrowsPointingInIcon, ArrowsPointingOutIcon,
} from '@heroicons/react/24/solid';
import { ChatTextarea } from '@components/common/Chat/ChatTextarea';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { Message, useStreamStateV2 } from '@components/templates/bia/hooks/useStreamStateV2';
import { v4 as uuidv4 } from 'uuid';
import { LayoutDefault } from '@components/layout/Layouts/LayoutDefault';
import type { JSX } from 'react';
import { ChatMessageComponent } from '@components/templates/homepage/chat/ChatMessageComponent';
import { ChatJobModal } from '@components/templates/homepage/chat/ChatJobModal';
import { useAnonSessionId } from '@hooks/common/useAnonSessionId';
import { useAppSelector } from '@store/index';
import { getJobBulkApplyFeed } from '@store/reducers/jobSelector';
import NiceModal from '@ebay/nice-modal-react';
import { BulkApplyModal } from '@components/templates/job/apply/BulkApplyModal';
// import { StartAiInterviewChatButton } from '@components/templates/ai-interview';
import ChatTalentModal from '@root/src/components/templates/homepage/chat/ChatTalentModal';
// import BestieAvatar from '@components/templates/bestie/BestieAvatar';
import sendGoogleTagEvent from '@hooks/useSendGoogleTagEvent';
import { cn } from '@utils/cn';
import { Transition } from '@headlessui/react';
import { useHeaderInfoData } from '@components/layout/Header/MainNavbar/data/useHeaderInfoData';
import { useSwrResumePersonalData } from '@hooks/useSwrApi/endpoints/resume/useSwrResumePersonalData';
import { StoriesSection } from '@components/templates/homepage/StoriesSection';
import { CompaniesSlider } from '@components/features/cards/employer/CompaniesSlider';
import { useSwrResumeLastWorkExperience } from '@hooks/useSwrApi/endpoints/resume';
import { useSwrJobSearchTooltip } from '@hooks/useSwrApi/endpoints/job';
import { showVoiceAgentModalWithAuth } from '@utils/voice-agent/showVoiceAgentModalWithAuth';

// Interface
// ************************************************************************************
interface HomepageProps {
  locale: Locale;
  clientCity: string;
  initialQuery?: string | null;
  initialInterview?: boolean;
}
/**
 * @description Homepage component
 * - **locale** - The locale of the app
 * - **eventsFeedServer** - The events list from GSSP
 * - **tagCloud** - The tag cloud list from GSSP
 * - **timestamp** - The timestamp for the events feed API call (in seconds)
 */
const Index = (props: HomepageProps) => {
  // Destructure props
  const {
    locale,
    clientCity,
    initialQuery = null,
    initialInterview = false,
  } = props;

  // The following script tag contains JSON-LD (JavaScript Object Notation for Linked Data)
  // structured data with the application/ld+json MIME type. JSON-LD is used to provide
  // machine-readable information about the website, enhancing its presentation
  // in search engine results and enabling better indexing by search engines.
  const ORG_SCHEMA = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    url: 'https://www.bestjobs.eu/',
    name: 'BestJobs',
    // SiteLinks Search Box
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `https://www.bestjobs.eu${route('app_job_canonical')}/{search_term_string}?utm_source=google&utm_medium=organic%20search`,
      },
      'query-input': 'required name=search_term_string',
    },
  });

  const agent = {
    id: '', // leave blank in order to use unified chat agent (hr + search)
  };
  const { t } = useTranslation('common');
  const { router } = useExtendedRouter();
  const addJobPath = '/job-add/edit?flow=v2';

  // Fetch authentication status and personal data early to resolve city
  const { headerInfoData } = useHeaderInfoData(locale);
  const isAuthenticated = !!headerInfoData;

  // Fetch personal data only if authenticated
  const { data: personalData } = useSwrResumePersonalData({
    locale,
    shouldFetch: isAuthenticated,
  });

  // Fetch last search keyword and last experience for personalization
  const { data: jobSearchTooltip } = useSwrJobSearchTooltip({
    locale,
    shouldFetch: isAuthenticated,
  });
  const { data: lastWorkExperience } = useSwrResumeLastWorkExperience({
    locale,
    shouldFetch: isAuthenticated,
  });

  // Resolve city value based on authentication and personal data
  const resolvedCity = useMemo(() => {
    if (!isAuthenticated) {
      // Not authenticated: use x-client-city header
      return clientCity;
    }

    // Authenticated: check resumeLocation from personal data
    const resumeLocation = personalData?.resumeLocation;

    if (!resumeLocation || resumeLocation.trim() === '') {
      // No location in personal data: use x-client-city header
      return clientCity;
    }

    // Use resumeLocation from personal data
    return resumeLocation;
  }, [isAuthenticated, personalData, clientCity]);

  const mainActionButtons = useMemo(() => [
    { label: t('homepage.chip.find_job'), key: 'homepage.chip.find_job' },
    { label: t('homepage.chip.update_profile'), key: 'homepage.chip.update_profile' },
    { label: t('homepage.chip.career_advice'), key: 'homepage.chip.career_advice' },
    { label: t('homepage.chip.hire_with_bestie'), key: 'homepage.chip.hire_with_bestie' },
  ], [t]);

  const goToAddJobFlow = useCallback(() => {
    if (isAuthenticated) {
      void router.push(addJobPath);
      return;
    }
    void router.push(`/login?targetPath=${encodeURIComponent(addJobPath)}`);
  }, [isAuthenticated, router]);

  const getSubOptions = (categoryKey: string, cityValue: string) => {
    const maxOptions = 4;
    const options = [];

    // Special handling for "Find jobs" category - personalize first sub-suggestion
    if (categoryKey === 'homepage.chip.find_job') {
      let keyword = '';
      const recentKeyword = jobSearchTooltip?.recentSearches?.[0]?.keyword
        || jobSearchTooltip?.recentSearches?.[0]?.jobSearch?.keyword;
      const savedKeyword = jobSearchTooltip?.savedSearches?.[0]?.keyword
        || jobSearchTooltip?.savedSearches?.[0]?.jobSearch?.keyword;

      // Priority 1: Last search keyword
      if (recentKeyword || savedKeyword) {
        keyword = String(recentKeyword || savedKeyword);
      } else if (lastWorkExperience?.title) {
        // Priority 2: Last experience
        keyword = String(lastWorkExperience.title);
      }

      // If we have a keyword, use the custom translation
      if (keyword) {
        const customLabel = t('homepage.chip.find_job_sub1_custom');
        const label = customLabel.replace('{{keyword}}', keyword);
        options.push({
          label,
          key: 'personalized_search',
        });
      }
      // Priority 3: Default - add sub1 normally below
    }

    for (let i = 1; i <= maxOptions; i++) {
      const key = `${categoryKey}_sub${i}`;
      let label = t(key);

      // i18next returns the key itself if translation is missing
      if (label !== key) {
        // For "Find jobs" category, skip sub1 if we already added a personalized option
        const skipPersonalizedSub1 = categoryKey === 'homepage.chip.find_job' && i === 1 && options.length > 0;

        if (!skipPersonalizedSub1) {
          // Replace {{city}} placeholder if present
          if (label.includes('{{city}}')) {
            label = label.replace('{{city}}', cityValue);
          }

          options.push({ label, key });
        }
      }
    }

    return options;
  };

  const {
    stream, startStream,
  } = useStreamStateV2(null);
  const initialMessageFromUrlSent = useRef(false);
  const anonSessionId = useAnonSessionId();
  const [talentsNavigationList, setTalentsNavigationList] = useState<ApiSchema<'ResumeCardData'>[]>([]);
  const autoStartMessage = initialInterview
    ? t('bestie.start.text.interview.prompt')
    : (initialQuery?.trim() || null);
  const [hasSubmittedFirstMessage, setHasSubmittedFirstMessage] = useState(!!autoStartMessage);
  const [firstMessageContent, setFirstMessageContent] = useState<string | null>(autoStartMessage);
  const [autoStartPending, setAutoStartPending] = useState(!!autoStartMessage);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);

  const headers = useMemo(() => ({
    'x-anon-session-id': anonSessionId,
  }), [anonSessionId]);

  const composer = useChatComposer({
    history: stream?.messages,
    onSubmit: async (text) => {
      if (!hasSubmittedFirstMessage) {
        setFirstMessageContent(text);
      }
      setHasSubmittedFirstMessage(true);
      const message = { id: uuidv4(), content: text, type: 'human' } as const;
      await startStream(message, agent.id || '', {
        headers,
        configurations: { user_locale: locale || 'ro' },
      });
    },
  });

  useEffect(() => {
    if (initialMessageFromUrlSent.current) return;
    if (!autoStartMessage || !anonSessionId) return;

    const message = {
      id: uuidv4(),
      content: autoStartMessage,
      type: 'human',
    };

    setHasSubmittedFirstMessage(true);
    void startStream(message, agent.id || '', {
      headers,
      configurations: { user_locale: locale || 'ro' },
    });

    router.replace('/bestie', { scroll: false });
    initialMessageFromUrlSent.current = true;
    setAutoStartPending(false);
  }, [autoStartMessage, anonSessionId, router, locale, startStream, agent.id, headers]);

  // Click outside to reset to main options
  useEffect(() => {
    if (!selectedCategory) return undefined;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is outside the chips and form area
      const isChipClick = target.closest('[data-chip-area]');
      const isFormClick = target.closest('form');

      if (!isChipClick && !isFormClick) {
        setSelectedCategory(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedCategory]);

  const jobsArray = useMemo(() => stream?.messages
    ?.filter((item) => (item?.response_metadata?.customData?.jobSearchResults?.items
      && item?.response_metadata?.customData?.jobSearchResults?.items?.length > 0))
    .reduce<{ items: ApiSchema<'JobItem'>[] | undefined }[]>((acc, item) => {
    acc.push({ items: item?.response_metadata?.customData?.jobSearchResults?.items?.map((i) => i.data) });
    return acc;
  }, []), [stream?.messages]);

  const [expandState, setExpandState] = useState<{ showToggle: boolean; isExpanded: boolean; toggle:() => void } | null>(null);

  const formElements = (
    <div className="w-full max-w-5xl">
      <div className={cn(
        'group relative flex items-end rounded-2xl border-2 bg-surface p-0.5 transition-all duration-700',
        hasSubmittedFirstMessage ? 'shadow-sm' : 'border-secondary shadow-lg shadow-secondary/30',
      )}
      >
        <ChatTextarea
          textareaRef={composer.textareaRef}
          input={composer.input}
          handleInputChange={composer.onChange}
          handleKeyDown={composer.onKeyDown}
          placeholder={t('homepage.chat.input.placeholder')}
          customWrapperStyle="flex-1 bg-white rounded-2xl"
          customTextareaStyle="transition-none bg-white text-gray-900 placeholder:text-gray-400 placeholder:text-base px-2 text-lg focus:ring-0 focus:ring-offset-0 outline-none border-none shadow-none whitespace-normal resize-none cursor-text pointer-events-auto placeholder:truncate placeholder:overflow-ellipsis leading-normal py-1.5 break-words"
          textareaSizeStyle="w-full min-h-10"
          textareaWrapperSizeStyle="w-full min-h-10"
          textareaAlignmentStyle="px-2 py-1.5"
          collapsedLineLimit={6}
          expandedLineLimit={15}
          onExpandStateChange={setExpandState}
        />
        {/* Right column: expand button above airplane button */}
        <div className="mr-1 flex shrink-0 flex-col items-center self-stretch">
          {expandState?.showToggle && (
            <button
              type="button"
              onClick={expandState.toggle}
              className="mt-1 shrink-0 rounded p-1 text-ink transition-colors hover:text-ink"
              aria-label={expandState.isExpanded ? 'Collapse' : 'Expand'}
            >
              {expandState.isExpanded
                ? <ArrowsPointingInIcon className="size-5" />
                : <ArrowsPointingOutIcon className="size-5" />}
            </button>
          )}
          <Button
            type="button"
            color="secondary"
            rounding="none"
            className="my-1 mt-auto size-12 shrink-0 rounded-2xl !p-3"
            onClick={() => {
              // Show microphone modal if no text and no first message sent yet
              if (!composer.input.trim()) {
                sendGoogleTagEvent('bestie_conversations_banner_call', 'Custom');
                showVoiceAgentModalWithAuth(agent.id, locale);
              } else {
                // Otherwise submit the text message
                void composer.submit();
              }
            }}
          >
            {!composer.input.trim() ? (
              <MicrophoneIcon className="size-5 text-white" />
            ) : (
              <PaperAirplaneIcon className="size-5 text-white" />
            )}
          </Button>
        </div>
      </div>
      {!hasSubmittedFirstMessage && (
        <div className="mb-2 mt-4 flex w-full flex-col gap-3 px-2 md:max-w-5xl md:px-0" data-chip-area>
          <div className={cn(
            'flex w-full gap-2',
            selectedCategory ? 'flex-col items-start' : 'flex-wrap justify-center',
          )}
          >
            {selectedCategory ? (
              // Show sub-options when a category is selected (vertical)
              getSubOptions(selectedCategory, resolvedCity).map((btn) => (
                <Chip
                  key={btn.key}
                  onClick={() => {
                    if (btn.key === 'homepage.chip.hire_with_bestie_sub1') {
                      goToAddJobFlow();
                      return;
                    }

                    if (!hasSubmittedFirstMessage) {
                      setFirstMessageContent(btn.label);
                    }
                    setHasSubmittedFirstMessage(true);
                    const message = { id: uuidv4(), content: btn.label, type: 'human' } as const;
                    void startStream(message, agent.id || '', {
                      headers,
                      configurations: { user_locale: locale || 'ro' },
                    });
                  }}
                  className="whitespace-normal rounded-2xl bg-surface px-3 py-2 text-left text-sm leading-relaxed text-ink shadow-sm hover:border-0 hover:!border-transparent hover:bg-secondary/75 hover:!text-surface md:text-base"
                >
                  {btn.label}
                </Chip>
              ))
            ) : (
              // Show main category chips when nothing is selected
              <>
                {mainActionButtons.map((btn) => (
                  <Chip
                    key={btn.key}
                    onClick={() => {
                      setSelectedCategory(btn.key);
                    }}
                    className="rounded-2xl bg-surface px-3 py-1.5 text-ink shadow-sm hover:border-0 hover:!border-transparent hover:bg-secondary/75 hover:!text-surface md:px-4 md:py-2"
                  >
                    {btn.label}
                  </Chip>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const bulkApplyFeed = useAppSelector(getJobBulkApplyFeed);

  useEffect(() => {
    if (bulkApplyFeed?.length) {
      void NiceModal.show(BulkApplyModal, { jobList: bulkApplyFeed, locale });
    }
  }, [bulkApplyFeed, locale]);


  const onCvDetailOpen = useCallback((message: Message) => {
    setTalentsNavigationList(message.response_metadata?.customData?.resumeSearchResults?.items || []);
  }, []);

  const messagesList = useMemo(() => {
    if (!stream?.messages) return [];

    // Use a Map to remove duplicates based on message id
    const reversed = [...new Map(stream.messages.map((m) => [m.id, m])).values()].reverse();

    // In flex-col-reverse layout, DOM index 0 = visually bottom, index N = visually top.
    // Tool results arrive before the AI text in streaming, so after reversing, text ends up
    // at index 0 (bottom) and results at index 1 (above). Swap consecutive pairs so text
    // appears above results in the UI.
    for (let i = 0; i < reversed.length - 1; i += 1) {
      const curr = reversed[i];
      const next = reversed[i + 1];
      const currHasResults = (curr?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (curr?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      const nextHasResults = (next?.response_metadata?.customData?.jobSearchResults?.items?.length ?? 0) > 0
        || (next?.response_metadata?.customData?.resumeSearchResults?.items?.length ?? 0) > 0;
      if (!currHasResults && curr?.type === 'ai' && nextHasResults) {
        reversed[i] = next;
        reversed[i + 1] = curr;
      }
    }

    return reversed.map((m) => (
      <div key={`message-container-${m.id}`} className="flex flex-col gap-4">
        <ChatMessageComponent
          key={`message-${m.id}`}
          message={{ ...m }}
          locale={locale}
          onInteraction={onCvDetailOpen}
        />
      </div>
    ));
  }, [stream?.messages, locale, onCvDetailOpen]);

  // Render page
  // ******************************
  const shouldShowChatView = (stream?.messages?.length ?? 0) > 0 || autoStartPending;

  return (
    <>
      {/* HEAD - title, meta, etc */}
      <Head>
        <title>
          {t('homepage.react.meta.title')}
        </title>
        <meta name="description" content={t('meta.homepage.description_react')} />
        <SanitizeHTML as="script" type="application/ld+json" htmlContent={ORG_SCHEMA} />
      </Head>


      {/* PAGE CONTENT */}
      <div className="flex grow flex-col bg-surface-100">
        {shouldShowChatView ? (
          <div className="flex min-h-0 grow flex-col">
            <div
              ref={messageContainerRef}
              className="container mx-auto flex min-h-0 grow flex-col-reverse gap-5 overflow-y-auto p-4 pb-6"
            >
              {(stream?.status === 'inflight' || autoStartPending) && <ChatTypingDots />}
              { messagesList }
            </div>

            <div className="bg-surface-100/95 backdrop-blur-sm">
              <form
                onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
                className="container mx-auto px-4 pb-4 pt-3"
              >
                <div className="mx-auto w-full max-w-5xl">
                  {formElements}
                </div>
              </form>
            </div>

            <ChatJobModal locale={locale} jobsArray={jobsArray} />
            <ChatTalentModal talentsArray={[{ items: talentsNavigationList }]} />
          </div>
        ) : (
          <div className="flex grow flex-col">
            <div className="flex grow flex-col items-center justify-end px-4 pb-4 pt-6 md:pt-12">
              <div className="flex w-full max-w-5xl flex-col items-center gap-2 text-center">
                {/* Animated first message bubble */}
                <Transition
                  as="div"
                  show={hasSubmittedFirstMessage && !!firstMessageContent && (stream?.messages?.length ?? 0) === 0}
                  enter="transition-all duration-700 ease-out"
                  enterFrom="opacity-0 translate-y-4 scale-95"
                  enterTo="opacity-100 translate-y-0 scale-100"
                  className="w-full"
                >
                  <div className="ml-auto w-fit max-w-[80%] rounded-2xl bg-secondary px-4 py-2 text-white">
                    {firstMessageContent}
                  </div>
                </Transition>

                <span className="text-2xl font-bold md:text-3xl">{t('homepage.hero.title')}</span>
                <span className="text-base md:text-lg">{t('homepage.hero.subtitle')}</span>

                <form
                  onSubmit={(e) => { e.preventDefault(); void composer.submit(); }}
                  className={cn(
                    'mt-4 w-full max-w-5xl transition-all duration-[400ms] ease-in-out',
                    hasSubmittedFirstMessage && 'sticky bottom-0 z-10 bg-surface-100/95 py-3 backdrop-blur-sm',
                  )}
                >
                  {formElements}
                </form>
              </div>
            </div>

            <div className="container mx-auto mt-8">
              <StoriesSection />
            </div>

            <div className="container mx-auto my-3">
              <CompaniesSlider />
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * @description Get the layout for the page
 * @param page
 */
Index.getLayout = function getLayout(page: JSX.Element) {
  return (
    <LayoutDefault>
      {page}
    </LayoutDefault>
  );
};


/**
 * @description Get the server-side props
 * @param context - The context object
 */
export const getServerSideProps: GetServerSideProps = async (context) => {
  // Props destructuring
  const { params, req, query } = context;

  const locale = gsspGetLocale(params);

  // Extract x-client-city header
  const clientCity = (req.headers['x-client-city'] as string) || '';

  // Return the props
  return {
    props: {
      ...(await serverSideTranslations(locale, ['common'])),
      locale,
      clientCity,
      initialQuery: typeof query.q === 'string' ? query.q : null,
      initialInterview: query.iw === '1',
    },
  };
};


/**
 * @description Export the page component.
 */
export default Index;
